import { useAttachments } from '$src/lib/hooks/useAttachments'
import { maintenanceLogHooks, maintenanceTaskHooks } from '$src/lib/trpcHooks'
import {
  getSeasonEndDate,
  TargetSeason,
} from '$src/modules/maintenance/YearCalendarCarousel/utils'
import { Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import {
  MaintenanceLogWithRelations,
  MaintenanceTaskWithSteps,
} from '@markku/types'
import { assertNotNil } from '@markku/utils'
import { useEffect, useState } from 'react'
import { TaskDetails } from './TaskDetails'
import { CompletionFormData, TaskFormData } from './TaskDetails/types'

interface TaskDetailsModalProps {
  task?: MaintenanceTaskWithSteps // For editing existing task
  log?: MaintenanceLogWithRelations // For editing existing log entry
  propertyId?: number | string // Required for creating new tasks; optional for editing (uses task/log propertyId)
  opened: boolean
  onClose: () => void
  defaultCompleted?: boolean // Auto-show completion form (for quick log entry)
  targetSeason?: TargetSeason // Season context for new tasks (for default deadline)
  onSave?: (taskDeadline?: string) => void
  onDeleted?: () => void // Called after successful deletion
}

export function TaskDetailsModal({
  task,
  log,
  propertyId: propertyIdProp,
  opened,
  onClose,
  defaultCompleted,
  targetSeason,
  onSave,
  onDeleted,
}: TaskDetailsModalProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [markAsCompleted, setMarkAsCompleted] = useState(false)

  // Initialize attachments hook with log attachments (saved to maintenance log, not task)
  const attachments = useAttachments('maintenance_log', log?.attachments)

  // Helper: Get form values from task/log data
  const getFormValues = (
    taskData?: typeof task,
    logData?: typeof log,
  ): TaskFormData & CompletionFormData => {
    // Calculate default deadline: use season end date if targetSeason is set, otherwise today
    const defaultDeadline = targetSeason
      ? getSeasonEndDate(targetSeason.season, targetSeason.year)
      : new Date().toISOString().split('T')[0]

    return {
      // Task fields
      name: taskData?.name || '',
      deadline: taskData?.deadline || defaultDeadline,
      category: taskData?.category || '',
      description: taskData?.description || '',
      estimatedCost: taskData?.estimatedCost
        ? parseFloat(taskData.estimatedCost)
        : undefined,
      // Completion fields
      completedDate:
        logData?.completedDate || new Date().toISOString().split('T')[0],
      notes: logData?.notes || '',
      actualCost: logData?.actualCost
        ? parseFloat(logData.actualCost)
        : undefined,
    }
  }

  // Helper: Get attachment info from log attachments
  const getAttachmentInfo = (logData?: typeof log) =>
    logData?.attachments?.map((a) => ({
      id: a.id,
      filename: a.filename,
      filesize: a.filesize,
      mimeType: a.mimeType,
      status: 'existing' as const,
      downloadUrl: a.downloadUrl,
    })) || []

  // Combined form for both task and completion data
  const form = useForm<TaskFormData & CompletionFormData>({
    initialValues: getFormValues(),
    validate: {
      name: (value) =>
        !value.trim() ? 'Tehtävän otsikko on pakollinen' : null,
      deadline: (value) => (!value ? 'Määräaika on pakollinen' : null),
      completedDate: (value) => {
        // Only validate completedDate if markAsCompleted is true
        if (markAsCompleted && !value) {
          return 'Suorituspäivämäärä on pakollinen'
        }
        return null
      },
    },
  })

  // Reset form when modal opens or data changes
  useEffect(() => {
    if (opened) {
      form.setValues(getFormValues(task, log))
      setMarkAsCompleted(defaultCompleted ?? Boolean(log))
      attachments.setInitialAttachments(getAttachmentInfo(log))
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, task, log, defaultCompleted])

  // Pre-fill actual cost with estimated cost when marking as completed
  useEffect(() => {
    if (
      markAsCompleted &&
      form.values.actualCost == null &&
      form.values.estimatedCost != null
    ) {
      form.setFieldValue('actualCost', form.values.estimatedCost)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAsCompleted])

  // Auto-sync deadline with completed date for new tasks that are already completed
  // (Case B: user immediately logs completed work)
  useEffect(() => {
    const isNewTask = !task?.id
    if (isNewTask && markAsCompleted && form.values.completedDate) {
      form.setFieldValue('deadline', form.values.completedDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAsCompleted, form.values.completedDate, task?.id])

  const { mutateAsync: createTask } = maintenanceTaskHooks.useCreate()
  const { mutateAsync: updateTask } = maintenanceTaskHooks.useUpdate()
  const { mutateAsync: deleteTask } = maintenanceTaskHooks.useDelete()
  const { mutateAsync: createLog } = maintenanceLogHooks.useCreate()
  const { mutateAsync: updateLog } = maintenanceLogHooks.useUpdate()
  const { mutateAsync: deleteLog } = maintenanceLogHooks.useDelete()

  // Determine propertyId: use task/log property if editing, otherwise use prop
  // Convert to number if it's a string (from URL params)
  const propertyId = assertNotNil(
    task?.propertyId || log?.propertyId || Number(propertyIdProp),
  )

  // Track initial state to detect changes
  const initiallyCompleted = Boolean(log)

  // Helper: Save task data (create or update)
  const saveTaskData = async () => {
    const taskData = {
      name: form.values.name,
      description: form.values.description,
      category: form.values.category,
      deadline: form.values.deadline,
      estimatedCost: form.values.estimatedCost?.toString(),
    }

    if (task?.id) {
      const result = await updateTask({ id: task.id, data: taskData })
      return result.id
    } else {
      const result = await createTask({
        data: { ...taskData, propertyId },
      })
      return result.id
    }
  }

  // Helper: Handle completion log (create, update, or delete)
  const handleCompletionLog = async (savedTaskId: number) => {
    const hasAttachments = attachments.getChanges().length > 0

    // If task was completed but user unchecked it - delete the log
    if (initiallyCompleted && !markAsCompleted) {
      if (log?.id) {
        await deleteLog({ id: log.id })
      }
      return
    }

    // Create or update log if: task is marked completed OR user added attachments
    if (!markAsCompleted && !hasAttachments) {
      return // No log needed
    }

    const logData = {
      propertyId,
      maintenanceTaskId: savedTaskId,
      taskNameSnapshot: form.values.name,
      descriptionSnapshot: form.values.description,
      completedDate: markAsCompleted ? form.values.completedDate : null,
      notes: markAsCompleted ? form.values.notes : null,
      actualCost:
        markAsCompleted && form.values.actualCost
          ? form.values.actualCost.toString()
          : null,
    }

    if (log?.id) {
      await updateLog({
        id: log.id,
        data: logData,
        attachments: attachments.getChanges(),
      })
    } else {
      await createLog({
        data: logData,
        attachments: attachments.getChanges(),
      })
    }
  }

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Poista tehtävä',
      centered: true,
      children: <Text>Haluatko varmasti poistaa tämän tehtävän?</Text>,
      labels: { confirm: 'Poista', cancel: 'Peruuta' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setIsSaving(true)
        try {
          // Delete both task and associated log in parallel
          await Promise.all([
            deleteTask({ id: assertNotNil(task?.id) }),
            ...(log?.id ? [deleteLog({ id: log.id })] : []),
          ])

          notifications.show({
            title: 'Onnistui',
            message: 'Tehtävä poistettu',
            color: 'green',
          })

          onDeleted?.()
          onClose()
        } catch (error) {
          console.error('Error deleting task:', error)
          notifications.show({
            title: 'Virhe',
            message: 'Tehtävän poistaminen epäonnistui',
            color: 'red',
          })
        } finally {
          setIsSaving(false)
        }
      },
    })
  }

  const handleSave = async () => {
    // Validate form
    if (form.validate().hasErrors) {
      notifications.show({
        title: 'Virhe',
        message: 'Tarkista lomakkeen tiedot',
        color: 'red',
      })
      return
    }

    setIsSaving(true)
    try {
      const savedTaskId = await saveTaskData()
      await handleCompletionLog(savedTaskId)

      notifications.show({
        title: 'Onnistui',
        message: 'Tehtävä tallennettu',
        color: 'green',
      })

      onSave?.(form.values.deadline)
      onClose()
    } catch (error) {
      console.error('Error saving task:', error)
      notifications.show({
        title: 'Virhe',
        message: 'Tehtävän tallentaminen epäonnistui',
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Build task object for TaskDetails component
  const taskDetailsData = {
    id: task?.id?.toString(),
    title: task?.name,
    description: task?.description || undefined,
    category: task?.category || undefined,
    deadline: task?.deadline || undefined,
    targetSeason: targetSeason?.season, // Only pass season name for mismatch check
    isUserTask: true,
    completed: !!log,
  }

  // Extract task and completion data from form
  const taskFormData: TaskFormData = {
    name: form.values.name,
    deadline: form.values.deadline,
    category: form.values.category,
    description: form.values.description,
    estimatedCost: form.values.estimatedCost,
  }

  const completionFormData: CompletionFormData = {
    completedDate: form.values.completedDate,
    notes: form.values.notes,
    actualCost: form.values.actualCost,
  }

  // Handlers for TaskDetails component
  const handleTaskDataChange = (data: TaskFormData) => {
    form.setValues({ ...form.values, ...data })
  }

  const handleCompletionDataChange = (data: CompletionFormData) => {
    form.setValues({ ...form.values, ...data })
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={task ? 'Muokkaa tehtävää' : 'Luo uusi tehtävä'}
      confirmProps={{
        label: 'Tallenna',
        onClick: handleSave,
        loading: isSaving,
      }}
      cancelProps={{
        label: 'Peruuta',
      }}
      deleteProps={
        task
          ? {
              onClick: handleDelete,
              loading: isSaving,
            }
          : undefined
      }
      showFooter={true}
    >
      <TaskDetails
        task={taskDetailsData}
        taskData={taskFormData}
        onTaskDataChange={handleTaskDataChange}
        completionData={completionFormData}
        onCompletionDataChange={handleCompletionDataChange}
        markAsCompleted={markAsCompleted}
        onMarkAsCompletedChange={setMarkAsCompleted}
        disabled={isSaving}
        attachments={attachments}
      />
    </AppModal>
  )
}
